// R7A-3: a Review checklist link (/list-your-offering/<step>/<id>#<field>)
// lands on its field: once the step has loaded its data and the field's
// position has settled, it is scrolled into view and focused. The router's
// `{ selector: hash }` ran once, before the step had loaded, so the page
// stayed at the top with focus on <body>.
//
// The field is found by id. A wrapper (a card, a radio group, a CKEditor
// container) is not focusable itself: its first checked radio, else its
// first form control, else its first focusable element, takes focus.
// R8A-4: a form control wins over a link or button in the same card (the
// images card focused its "Send these specs" help link), and a card may
// name the control to land on with `data-focus-target="<id>"` (the mixed
// terms card lands on the Reg CF minimum, not on "Copy from 506(c)").

const FOCUSABLE =
  'input:not([type="hidden"]):not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), button:not([disabled]), [contenteditable="true"], ' +
  'a[href], [tabindex]:not([tabindex="-1"])';

const FORM_CONTROL =
  'input:not([type="hidden"]):not([disabled]), select:not([disabled]), ' +
  'textarea:not([disabled]), [contenteditable="true"]';

function byHash(hash, doc) {
  const raw = String(hash || "").replace(/^#/, "");
  if (!raw) return null;
  let id = raw;
  try {
    id = decodeURIComponent(raw);
  } catch (_) {
    // Keep the raw id.
  }
  return doc.getElementById(id);
}

function isFocusable(el) {
  return Boolean(el && typeof el.matches === "function" && el.matches(FOCUSABLE));
}

// Not inside something hidden with `d-none` / `hidden` (no layout needed).
function isShown(el) {
  return !(typeof el.closest === "function" && el.closest(".d-none, [hidden]"));
}

function firstShown(el, selector) {
  if (typeof el.querySelectorAll !== "function") return null;
  return Array.from(el.querySelectorAll(selector)).find(isShown) || null;
}

// The element that should take focus for the field `el`.
export function focusTargetFor(el) {
  if (!el) return null;
  if (isFocusable(el)) return el;
  const named = typeof el.getAttribute === "function" && el.getAttribute("data-focus-target");
  if (named && el.ownerDocument) {
    const target = el.ownerDocument.getElementById(named);
    if (target && target !== el && isFocusable(target) && isShown(target)) return target;
  }
  if (typeof el.querySelector !== "function") return el;
  return (
    firstShown(el, 'input[type="radio"]:checked:not([disabled])') ||
    firstShown(el, FORM_CONTROL) ||
    firstShown(el, FOCUSABLE) ||
    el
  );
}

function pageTop(el) {
  const rect = el.getBoundingClientRect();
  const y = typeof window !== "undefined" ? window.pageYOffset || 0 : 0;
  return Math.round(rect.top + y);
}

// Resolves with the field once it exists and its position has not moved for
// `stableMs` (the step's data load re-lays the page out), or null after
// `timeoutMs`. `isReady()` lets a caller add its own condition.
export function waitForField(hash, options = {}) {
  const doc = options.document || (typeof document !== "undefined" ? document : null);
  const timeoutMs = options.timeoutMs == null ? 8000 : options.timeoutMs;
  const stableMs = options.stableMs == null ? 300 : options.stableMs;
  const intervalMs = options.intervalMs == null ? 100 : options.intervalMs;
  const isReady = options.isReady || (() => true);
  if (!doc) return Promise.resolve(null);
  const start = Date.now();
  return new Promise((resolve) => {
    let lastTop = null;
    let stableSince = null;
    const tick = () => {
      const el = byHash(hash, doc);
      if (el && isReady()) {
        const top = pageTop(el);
        if (top === lastTop) {
          if (stableSince === null) stableSince = Date.now();
          if (Date.now() - stableSince >= stableMs) {
            resolve(el);
            return;
          }
        } else {
          lastTop = top;
          stableSince = null;
        }
      } else {
        lastTop = null;
        stableSince = null;
      }
      if (Date.now() - start >= timeoutMs) {
        resolve(null);
        return;
      }
      setTimeout(tick, intervalMs);
    };
    tick();
  });
}

// Scroll the field into view and focus it. Returns the focused element.
export function revealField(el) {
  if (!el) return null;
  if (typeof el.scrollIntoView === "function") {
    try {
      el.scrollIntoView({ block: "center" });
    } catch (_) {
      el.scrollIntoView();
    }
  }
  const target = focusTargetFor(el);
  if (target && typeof target.focus === "function") {
    if (!isFocusable(target) && !target.hasAttribute("tabindex")) {
      // A wrapper with no control inside: make it focusable for this jump.
      target.setAttribute("tabindex", "-1");
    }
    try {
      target.focus({ preventScroll: true });
    } catch (_) {
      target.focus();
    }
  }
  return target;
}

// Wait for the field named by `hash`, then reveal it.
export async function scrollToField(hash, options = {}) {
  const el = await waitForField(hash, options);
  return revealField(el);
}

// The builder steps whose hash links name a field.
export function isBuilderFieldLink(route) {
  return Boolean(
    route && route.hash && /^\/list-your-offering\//.test(route.path || "")
  );
}
